import { forwardRef, useImperativeHandle, ChangeEvent, useState, useCallback, useEffect, useMemo, useRef } from 'react'
import styled, { css } from 'styled-components'
import DaumPostcode from 'react-daum-postcode'
import { InputText, InputTextDiv, InputTextSection, InputTextTitle } from '../input/InputText'
import { ToggleRadioButton } from '../input/InputRadioButton'
import { InputTextArea } from '../../components/input/InputTextArea'
import Dropdown from '../../components/dropdown/Dropdown'

import { AddressItem } from '../../@types/order'

type AddressInputSectionProps = {
  addAddress: boolean
  editAddress?: AddressItem | null
  defaultAddress?: AddressItem | null
}

type AddressError = {
  receiveAddressName?: boolean
  receiveName?: boolean
  receiveZip?: boolean
  receiveAddress_1?: boolean
  receiveAddress_2?: boolean
  receivePhone?: boolean
}

const AddressInputSection = forwardRef(({ addAddress, editAddress, defaultAddress }: AddressInputSectionProps, ref) => {
  // State
  const [visibleAddressFinder, setVisibleAddressFinder] = useState<boolean>(false)

  const [receiveDefaultAddress, setReceiveDefaultAddress] = useState<string>('Y')

  const [receiveAddressName, setReceiveAddressName] = useState<string>('')
  const [receiveName, setReceiveName] = useState<string>('')
  const [receiveZip, setReceiveZip] = useState<string>('')
  const [receiveAddress_1, setReceiveAddress_1] = useState<string>('')
  const [receiveAddress_2, setReceiveAddress_2] = useState<string>('')
  const [receivePhone, setReceivePhone] = useState<string>('')
  const [receiveMemo, setReceiverMemo] = useState<string>('')
  const [receiveMemoCustom, setReceiverMemoCustom] = useState('')
  const [receiveAddressType, setReceiveAddressType] = useState<string>('')
  const [isFocus, setIsFocus] = useState<boolean>(false)
  const [error, setError] = useState<AddressError>({})

  // Ref
  const deliveryAddressNameRef = useRef<HTMLInputElement>(null)
  const deliveryNameRef = useRef<HTMLInputElement>(null)
  const deliveryAddressRef = useRef<HTMLInputElement>(null)
  const deliveryAddressDetailRef = useRef<HTMLInputElement>(null)
  const deliveryPhoneRef = useRef<HTMLInputElement>(null)
  const receiveMemoCustomRef = useRef<HTMLTextAreaElement>(null)

  // Memo
  const deliveryMemo = useMemo(
    () => ['문 앞에 놓아주세요', '부재 시 연락 부탁드려요', '배송 전 미리 연락해 주세요', '직접 입력'],
    []
  )

  const defaultAddressHandler = (value: string | null) => {
    setReceiveDefaultAddress(value === 'Y' ? 'Y' : 'N')
  }

  const handleComplete = (data: any) => {
    const { address, userSelectedType, bname, buildingName, zonecode, jibunAddress } = data

    // 도로명 주소 + (동/건물명)
    const extra = [bname, buildingName].filter(Boolean).join(', ')
    const fullAddress = userSelectedType === 'R' && extra ? `${address} (${extra})` : jibunAddress

    setReceiveZip(zonecode)
    setReceiveAddress_1(fullAddress) // 도로명 주소
    setReceiveAddress_2('') // 상세 주소는 직접 입력이므로 초기화
    setReceiveAddressType(userSelectedType)
    setVisibleAddressFinder(false)
  }

  // 실시간 입력 핸들러
  const handleAddressNameChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setReceiveAddressName(value)
    setError((prev) => ({
      ...prev,
      receiveAddressName: value.trim() === '',
    }))
  }, [])

  const handleNameChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setReceiveName(value)
    setError((prev) => ({
      ...prev,
      receiveName: value.trim() === '', // 비어있으면 true, 아니면 false
    }))
  }, [])

  const handleAddress2Change = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setReceiveAddress_2(value)
    setError((prev) => ({
      ...prev,
      receiveAddress_2: value.trim() === '', // 비어있으면 true, 아니면 false
    }))
  }, [])

  const handlePhoneChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setReceivePhone(value)
    setError((prev) => ({
      ...prev,
      receivePhone: !/^\d{10,11}$/.test(value.replace(/-/g, '')),
    }))
  }, [])

  // 유효성 검사 함수
  const validate = useCallback(() => {
    const newError: AddressError = {
      receiveAddressName: receiveAddressName.trim() === '',
      receiveName: receiveName.trim() === '',
      receiveZip: receiveZip.trim() === '',
      receiveAddress_1: receiveAddress_1.trim() === '',
      receiveAddress_2: receiveAddress_2.trim() === '',
      receivePhone: !/^\d{10,11}$/.test(receivePhone.replace(/-/g, '')),
    }
    setError(newError)

    // 에러 발생 시, 가장 먼저 에러난 곳에 포커스 및 alert
    if (newError.receiveAddressName) {
      deliveryAddressNameRef.current?.focus()
      alert('배송지 명을 입력해 주세요.')
      return false
    }
    if (newError.receiveName) {
      deliveryNameRef.current?.focus()
      alert('받으시는 분 성함을 입력해 주세요.')
      return false
    }
    if (newError.receiveZip) {
      deliveryAddressRef.current?.focus()
      alert('우편번호를 입력해 주세요.')
      return false
    }
    if (newError.receiveAddress_1) {
      deliveryAddressRef.current?.focus()
      alert('주소를 입력해 주세요.')
      return false
    }
    if (newError.receiveAddress_2) {
      deliveryAddressDetailRef.current?.focus()
      alert('상세 주소를 입력해 주세요.')
      return false
    }
    if (newError.receivePhone) {
      deliveryPhoneRef.current?.focus()
      alert('휴대폰 번호를 정확히 입력해 주세요.')
      return false
    }
    return true
  }, [receiveAddressName, receiveName, receiveZip, receiveAddress_1, receiveAddress_2, receivePhone])

  // 수정 시에 작동
  useEffect(() => {
    if (editAddress) {
      setReceiveAddressName(editAddress.addressName || '')
      setReceiveName(editAddress.receiverName || '')
      setReceiveZip(editAddress.receiverZipCd || '')
      setReceiveAddress_1(editAddress.receiverAddress || '')
      setReceiveAddress_2(editAddress.receiverDetailAddress || '')
      setReceivePhone(editAddress.receiverContact1?.replace(/-/g, '') || '')
      setReceiveDefaultAddress(editAddress.defaultYn === 'Y' ? 'Y' : 'N')

      // 배송메모 처리
      const memo = (editAddress.addressMemo || '').trim()
      const dropdownValues = deliveryMemo.map((v) => v.trim())
      if (!memo) {
        setReceiverMemo('')
        setReceiverMemoCustom('')
      } else if (dropdownValues.includes(memo) && memo !== '직접 입력') {
        setReceiverMemo(memo)
        setReceiverMemoCustom('')
      } else if (memo === '직접 입력') {
        setReceiverMemo('직접 입력')
        setReceiverMemoCustom('')
      } else {
        setReceiverMemo('직접 입력')
        setReceiverMemoCustom(memo)
      }
    }
  }, [editAddress, deliveryMemo])

  useImperativeHandle(
    ref,
    () => ({
      validate,
      getValues: () => {
        // 배송메모 값 결정
        let memo = receiveMemo === '직접 입력' ? receiveMemoCustom : receiveMemo
        if (!memo) {
          // 아무것도 입력되지 않은 경우 드롭다운 첫 번째 값 사용
          memo = deliveryMemo[0]
        }
        return {
          receiveDefaultAddress,
          receiveAddressName,
          receiveName,
          receiveZip,
          receiveAddress_1,
          receiveAddress_2,
          receivePhone,
          receiveMemo: memo,
          receiveAddressType,
        }
      },
    }),
    [
      receiveDefaultAddress,
      receiveAddressName,
      receiveName,
      receiveZip,
      receiveAddress_1,
      receiveAddress_2,
      receivePhone,
      receiveMemo,
      receiveMemoCustom,
      receiveAddressType,
      validate,
      deliveryMemo,
    ]
  )

  if (!addAddress) return null

  return (
    <>
      {/* 슬라이드 우편번호 찾기: 입력폼 바로 위에 위치 */}
      {visibleAddressFinder && (
        <>
          {/* <SlideDim visible={visibleAddressFinder} onClick={() => setVisibleAddressFinder(false)} /> */}
          <SlidePostcode $visible={visibleAddressFinder}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '5px 0 0' }}>
              <button
                onClick={() => setVisibleAddressFinder(false)}
                style={{ background: 'none', border: 'none', width: '48px', height: '48px', padding: '12px' }}
              >
                <img src='/images/icon_close.png' alt='닫기' style={{ width: '100%', height: '100%' }} />
              </button>
            </div>
            <div style={{ flex: 1, minHeight: 0 }}>
              <DaumPostcode onComplete={handleComplete} style={{ height: '100%', margin: '0 auto' }} />
            </div>
          </SlidePostcode>
        </>
      )}
      <SectionWrap>
        <InputTextSection>
          <InputTextDiv style={{ display: 'flex', alignItems: 'center', margin: '10px 0' }}>
            <ToggleRadioButton
              name='myRadio'
              value='Y'
              checked={receiveDefaultAddress === 'Y'}
              onChange={defaultAddressHandler}
              label='기본 배송지로 설정'
              size='small'
              disabled={editAddress ? false : !defaultAddress}
            />
          </InputTextDiv>
          <InputTextDiv>
            <InputTextTitle>배송지 명</InputTextTitle>
            <InputText
              ref={deliveryAddressNameRef}
              placeholder='배송지 명을 입력해 주세요.'
              onChange={handleAddressNameChange}
              value={receiveAddressName}
              $error={error.receiveAddressName}
            />
          </InputTextDiv>
          <InputTextDiv>
            <InputTextTitle>받으시는 분</InputTextTitle>
            <InputText
              ref={deliveryNameRef}
              placeholder='성함을 입력해 주세요.'
              onChange={handleNameChange}
              value={receiveName}
              $error={error.receiveName}
            />
          </InputTextDiv>
          <InputTextDiv>
            <InputTextTitle style={{ alignSelf: 'start', marginTop: 10 }}>주소</InputTextTitle>
            <AddressInputGroup>
              <AddressInputRows>
                <AddressZipRow>
                  <AddressInputField
                    ref={deliveryAddressRef}
                    type='text'
                    id='address_1'
                    name='address_1'
                    readOnly
                    value={receiveZip}
                    $error={error.receiveZip}
                    $isPostField
                  />
                  <AddressFindButton type='button' id='address_btn' onClick={() => setVisibleAddressFinder(true)}>
                    우편번호 찾기
                  </AddressFindButton>
                </AddressZipRow>
                <AddressInputField
                  type='text'
                  id='address_2'
                  name='address_2'
                  readOnly
                  value={receiveAddress_1}
                  $error={error.receiveAddress_1}
                />
              </AddressInputRows>
              <AddressInputField
                ref={deliveryAddressDetailRef}
                type='text'
                id='address_3'
                name='address_3'
                placeholder='상세주소를 입력해주세요'
                value={receiveAddress_2}
                $focus={isFocus}
                $error={error.receiveAddress_2}
                $addressInput
                onChange={handleAddress2Change}
                onFocus={() => setIsFocus(true)}
                onBlur={() => setIsFocus(false)}
              />
            </AddressInputGroup>
          </InputTextDiv>
          <InputTextDiv>
            <InputTextTitle>휴대폰 번호</InputTextTitle>
            <InputText
              ref={deliveryPhoneRef}
              type='number'
              placeholder='‘-’을 제외하고 입력해 주세요.'
              onChange={handlePhoneChange}
              value={receivePhone?.replaceAll('-', '')}
              $error={error.receivePhone}
            />
          </InputTextDiv>
          <InputTextDiv>
            <InputTextTitle style={{ alignSelf: 'start', marginTop: 10 }}>배송 메시지</InputTextTitle>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                gap: 8,
              }}
            >
              <Dropdown setReceiverMemo={setReceiverMemo} data={deliveryMemo} value={receiveMemo} />
              {(receiveMemo === '직접 입력' || (!!receiveMemo && !deliveryMemo.includes(receiveMemo))) && (
                <InputTextArea
                  ref={receiveMemoCustomRef}
                  onChange={(e) => setReceiverMemoCustom(e.target.value)}
                  value={receiveMemoCustom}
                  placeholder='요청사항을 입력해 주세요. (30자 이내)'
                  maxLength={30}
                />
              )}
            </div>
          </InputTextDiv>
        </InputTextSection>
      </SectionWrap>
    </>
  )
})

const SectionWrap = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: 20px;
`

// 주소 입력 영역 스타일
const AddressInputGroup = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 8px;
`

const AddressInputRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const AddressZipRow = styled.div`
  display: flex;
  gap: 8px;
  justify-content: space-between;
`

const AddressInputField = styled.input<{
  $error?: boolean
  $focus?: boolean
  $isPostField?: boolean
  $addressInput?: boolean
}>`
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: ${({ $focus, $addressInput }) => ($focus || $addressInput ? 'var(--colBlack)' : 'var(--colGray6)')};
  padding: 10px 14px;
  border-radius: 5px;
  border: 1px solid ${({ $error, $focus }) => ($error ? 'red' : $focus ? 'var(--colBlack)' : 'var(--colGray3)')};
  background-color: ${({ readOnly }) => (readOnly ? 'var(--colGray1)' : 'var(--colWhite)')};
  flex: 1 1 auto;
  width: ${({ $isPostField }) => ($isPostField ? 'calc(100% - 110px)' : '100%')};
  min-width: 0;
`

const AddressFindButton = styled.button`
  flex: 0 0 102px;
  font-size: 13px;
  font-weight: 500;
  line-height: 16px;
  color: var(--colWhite);
  padding: 12px 14px;
  border-radius: 5px;
  max-width: 100px;
  width: 102px;
  background-color: var(--colBlack);
  border: 0;
  font-family: Pretendard, sans-serif;
`

const SlidePostcode = styled.div<{ $visible: boolean }>`
  position: absolute;
  top: 0;
  right: 0;
  width: 100%;
  height: 100%;
  background: var(--colWhite, #fff);
  z-index: 10;
  box-shadow: -2px 0 16px rgba(0, 0, 0, 0.15);
  transform: translateX(100%);
  transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  ${({ $visible }) =>
    $visible &&
    css`
      transform: translateX(0);
    `}
  display: flex;
  flex-direction: column;
`

AddressInputSection.displayName = 'AddressInputSection'
export default AddressInputSection
